import { useRef, useEffect, useCallback, useState, useMemo } from "react"
import useCreativePickerQuery, {
  type CreativePickerItem,
} from "../hooks/useCreativePickerQuery"
import CreativePickerCard from "./CreativePickerCard"

interface CreativeGridProps {
  mode: "ads" | "organic"
  dateRange: [Date, Date]
  sortBy: string
  sortDir: "asc" | "desc"
  selectedIds: Set<string>
  onToggleSelect: (id: string, item: CreativePickerItem) => void
  columnFilters?: Array<{ id: string; value: string | string[] }>
  page: number
  onLoadMore: () => void
}

const ITEMS_PER_PAGE = 15

export default function CreativeGrid({
  mode,
  dateRange,
  sortBy,
  sortDir,
  selectedIds,
  onToggleSelect,
  columnFilters,
  page,
  onLoadMore,
}: CreativeGridProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Track a fingerprint of query params that should reset accumulated items
  const queryFingerprint = useMemo(
    () => JSON.stringify({ mode, dateRange, sortBy, sortDir, columnFilters }),
    [mode, dateRange, sortBy, sortDir, columnFilters],
  )

  // Accumulated items across pages — reset when query params change
  const [accumulated, setAccumulated] = useState<CreativePickerItem[]>([])
  const prevFingerprintRef = useRef(queryFingerprint)

  // Fetch only the current page
  const {
    items: pageItems,
    totalCount,
    isLoading,
    isResolving,
  } = useCreativePickerQuery({
    mode,
    dateRange,
    sortBy,
    sortDir,
    offset: page * ITEMS_PER_PAGE,
    limit: ITEMS_PER_PAGE,
    columnFilters,
  })

  // When query params change, reset accumulated items
  useEffect(() => {
    if (prevFingerprintRef.current !== queryFingerprint) {
      prevFingerprintRef.current = queryFingerprint
      setAccumulated([])
    }
  }, [queryFingerprint])

  // Append new page items to accumulated list
  useEffect(() => {
    if (pageItems.length === 0) return

    setAccumulated(prev => {
      // If page is 0 and fingerprint just changed, start fresh
      if (page === 0 && prev.length === 0) {
        return pageItems
      }

      // Append only items we don't already have
      const existingIds = new Set(prev.map(i => i.id))
      const newItems = pageItems.filter(i => !existingIds.has(i.id))
      if (newItems.length === 0) return prev
      return [...prev, ...newItems]
    })
  }, [pageItems, page])

  const hasMore = accumulated.length < totalCount

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasMore && !isLoading) {
        onLoadMore()
      }
    },
    [hasMore, isLoading, onLoadMore],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: "200px",
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleIntersect])

  if (isLoading && accumulated.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-3">
        <span className="loading loading-spinner loading-md text-primary" />
        <p className="text-sm text-basic-light/50">Loading creative...</p>
      </div>
    )
  }

  if (!isLoading && accumulated.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-sm text-basic-light/50">
          No {mode === "ads" ? "ads" : "organic posts"} found for this date
          range.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="grid grid-cols-5 gap-3">
        {accumulated.map(item => (
          <CreativePickerCard
            key={`${item.type}-${item.id}`}
            item={item}
            isSelected={selectedIds.has(item.id)}
            onToggle={() => onToggleSelect(item.id, item)}
          />
        ))}
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-4" />

      {(isLoading || isResolving) && accumulated.length > 0 && (
        <div className="flex justify-center py-4">
          <span className="loading loading-spinner loading-sm text-primary" />
        </div>
      )}
    </div>
  )
}
